<template>
	<div class="PersonalisationField">
		<div class="row">
			<v-menu>
				<template v-slot:activator="{ props }">
					<v-text-field v-bind="props" variant="outlined" density="compact" maxLength="35"
						v-model="personalisationField.fields.label[selectedLocale]"
						:error-messages="labelErrors[selectedLocale] || additionalPersonalisation?.fields.personalisationGroup?.errors?.title || ''"
						:persistent-hint="true"
						:hint="$t('text.personalisationFieldCharacterRestrictionHint')"
						style="min-width: 150px; flex: 1;"
						@blur="validateLabel(selectedLocale)"
						@input="validateLabel(selectedLocale)"
					/>
				</template>
				<v-list>
					<v-list-item v-for="(locale, l) in locales" :key="locale.code">
						<v-row align="center" no-gutters>
							<LanguageFlag v-model="locale.code" style="cursor:pointer; margin-right:-8px; margin-top:-24px" />
							<v-text-field @click.stop="" @keydown.stop="" variant="outlined" maxLength="35"
								v-model="personalisationField.fields.label[locale.code]"
								:autofocus="l === 0"
								:error-messages="labelErrors[locale.code] || ''"
								@blur="validateLabel(locale.code)"
								@input="validateLabel(locale.code)"
							/>
						</v-row>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-select variant="outlined" hide-details density="compact"
				v-model="personalisationField.fields.inputType.de"
				:error-messages="additionalPersonalisation?.fields.personalisationGroup.errors?.inputType ?? ''"
				:items="allowedTypes"
				:item-title="item => item.value ? $t('text.' + item.value) : ''"
				style="min-width: 150px; flex-grow: 0;"
			/>

			<div v-if="additionalPersonalisation"
				style="display: flex; gap: 10px; align-items: center;"
			>
				<mys-switch v-model="additionalPersonalisation.fields.required.de"
					:label="$t('text.mandatory')"
					style="flex-grow: 0;"
				/>

				<v-btn elevation="0" @click="$emit('remove')">
					<v-icon size="24px">mdi-delete</v-icon>
				</v-btn>
			</div>
		</div>
		<div v-if="personalisationField.fields?.options && personalisationField.fields.inputType.de === 'List'"
			style="padding: 12px; display: flex; flex-direction: column; gap: 8px; width: 350px;"
		>
			<!-- Option Item -->
			<div class="row" v-for="(option) in personalisationField.fields.options.de" :key="option.key"
				@click.stop=""
				@keydown.stop=""
			>
				<v-menu>
					<template v-slot:activator="{ props }">
						<div v-bind="props" class="row">
							<v-icon>mdi-circle-medium</v-icon>
							<v-text-field variant="outlined" density="compact"
								v-model="option.values[selectedLocale]"
								:error-messages="optionErrors[`${option.key}_${selectedLocale}`] || option.errors || ''"
								:persistent-hint="true"
								:hint="$t('text.personalisationFieldCharacterRestrictionHint')"
								@keydown.stop=""
								@blur="validateOption(option, selectedLocale)"
								@input="validateOption(option, selectedLocale)"
							/>
							<v-icon @click.stop="removeOption(option)">mdi-delete</v-icon>
						</div>
					</template>

					<v-list>
						<v-list-item v-for="(locale, l) in locales" :key="locale.code">
							<v-row align="center" no-gutters>
								<LanguageFlag v-model="locale.code" style="cursor:pointer;margin-right:-8px;margin-top:-24px" />
								<v-text-field @click.stop="" @keydown.stop="" variant="outlined"
									:autofocus="l === 0"
									v-model="option.values[locale.code]"
									:error-messages="optionErrors[`${option.key}_${locale.code}`] || ''"
									@blur="validateOption(option, locale.code)"
									@input="validateOption(option, locale.code)"
								/>
							</v-row>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>

			<div style="padding-left: 18px;">
				<v-btn class="gradientButton" style="margin-left: 15px;" elevation="0" @click.stop="addOption()">
					<v-icon>mdi-plus</v-icon>{{ $t('text.addOption') }}
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import LanguageFlag from '../../components/common/LanguageFlag.vue'
import Common from '../../mixins/Common.vue'

export default {
	components: { LanguageFlag },
	mixins: [ Common ],
	props: {
		additionalPersonalisation: Object,
		index: Number,
	},
	data: () => ({
		allowedTypes: [ { value: 'List' }, { value: 'Text' } ],
		labelErrors: {},
		optionErrors: {},
	}),
	computed: {
		personalisationField() {
			return this.additionalPersonalisation?.fields.personalisationGroup.de.fields.personalisationFields.de[0]
		},
		options() {
			return this.personalisationField.fields.options.de
		},
		hasValidationErrors() {
			const hasLabelErrors = Object.values(this.labelErrors).some(error => error !== '')
			const hasOptionErrors = Object.values(this.optionErrors).some(error => error !== '')
			return hasLabelErrors || hasOptionErrors
		},
	},
	watch: {
		// we also need to update the title of the personalisation group..
		personalisationField: {
			deep: true,
			handler(n) {
				if (this.additionalPersonalisation?.fields?.personalisationGroup?.de?.fields && n?.fields?.label) {
					this.additionalPersonalisation.fields.personalisationGroup.de.fields.title = n.fields.label
				}
			},
		},
		'personalisationField.fields.inputType.de'(n) {
			if (n === 'List' && !this.personalisationField.fields.options?.de) {
				this.personalisationField.fields.options = { de: [ { key: 0, values: { de: '', en: '', fr: '', it: '', nl: '' } } ] }
			}
		},
	},
	methods: {
		hasAnyText(values) {
			return Object.values(values ?? {}).some(value => `${value ?? ''}`.trim().length > 0)
		},
		validateCharacters(value) {
			// Allow alphanumeric, spaces, hyphens, and basic punctuation but restrict special characters like &, <, >, etc.
			const validPattern = /^[a-zA-Z0-9\s\-_.,:;'"+()\/äöüÄÖÜßàâæçéèêëïîôùûüÿœÀÂÆÇÉÈÊËÏÎÔÙÛÜŸŒáéíóúñÁÉÍÓÚÑàèìòùÀÈÌÒÙ]*$/
			return validPattern.test(value)
		},
		validateLabel(locale) {
			const value = this.personalisationField.fields.label[locale]
			if (!this.hasAnyText(this.personalisationField.fields.label)) {
				this.labelErrors[locale] = this.$t('text.missingFieldsError')
			}
			else if (value && !this.validateCharacters(value)) {
				this.labelErrors[locale] = this.$t('text.personalisationFieldCharacterRestriction')
			}
			else {
				this.labelErrors[locale] = ''
			}
			this.$emit('validation-change', !this.hasValidationErrors)
		},
		validateOption(option, locale) {
			const value = option.values[locale]
			if (!this.hasAnyText(option.values)) {
				this.optionErrors[`${option.key}_${locale}`] = this.$t('text.missingFieldsError')
			}
			else if (value && !this.validateCharacters(value)) {
				this.optionErrors[`${option.key}_${locale}`] = this.$t('text.personalisationFieldCharacterRestriction')
			}
			else {
				this.optionErrors[`${option.key}_${locale}`] = ''
			}
			this.$emit('validation-change', !this.hasValidationErrors)
		},
		addOption() {
			let index = this.options.length
			this.options.push({ key: index, values: { de: '', en: '', fr: '', it: '', nl: '' } })
		},
		removeOption(option) {
			let index = this.options.findIndex(x => x == option)
			this.options.splice(index, 1)
			// update keys of remaining options
			for (let i = 0; i < this.options.length; i++) {
				this.options[i].key = i
			}
		},
	},
	mounted() {
		this.validateLabel(this.selectedLocale)
		if (this.personalisationField.fields.inputType.de === 'List') {
			for (const option of this.options ?? []) {
				this.validateOption(option, this.selectedLocale)
			}
		}
	},
}
</script>

<style scoped>
.PersonalisationField { width: 100%; }
.row { display: flex; gap: 8px; align-items: flex-start; width: 100%; }
.row > .Field { border-left: none; padding: 0; }
</style>
